import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import cn from "classnames";
import { useDispatch } from "react-redux";

import { editTask } from "../../../../../store/plan/actions";
import { post } from "../../../../../actions/general";
import { IRhytNotationData, ITaskEditorProps } from "../../../../../../common/types";
import { rhytPlayStyleOptions } from "../../../../../../common/parameters";
import { ICheckboxEvent, ISelectEvent } from "../../../../../types";
import { setItem, hasItem } from "../../../../../cookie";

import EditorBar, { FlexLeft, FlexRight, Center, Changer } from "../../../EditorBar";
import ScoreEditor from "../../../../Score";
import { Checkbox, Select } from "../../../../Form";

import getScore from "../../Common/getScore";
import getScale from "../../Common/getScale";
import getTempo from "../../Common/getTempo";
import ChangeScale from "../../Common/ChangeScale";
import ChangeTempo from "../../Common/ChangeTempo";
import useExerciseEditor from "../../Common/useExerciseEditor";

import messages from "./messages";
import { SelectWrapper } from "./styles";
import ChangeColor from "../../Common/ChangeColor";

const RhytNotationEditor = ({ taskIndex, plan, toggleInlineEditor, publicView, inlineEditorOpen }: ITaskEditorProps) => {
    const dispatch = useDispatch();
    const { formatMessage } = useIntl();
    const {
        openChanger,
        playable,
        handleChangeOpen,
        setPopupRef,
        storeScore,
        changeTempo,
        setPlayable,
        exportScore,
    } = useExerciseEditor({ plan, inlineEditorOpen, taskIndex });
    const changeRhytPlayStyle = (e: ISelectEvent) => {
        let rhytPlayStyle = e.target.value as IRhytNotationData["rhytPlayStyle"];
        dispatch(editTask({ taskIndex, changingProp: "rhytPlayStyle", val: rhytPlayStyle }));
        dispatch(post("rhyt-notation-play-style", {
            planId: plan._id,
            index: taskIndex,
            rhytPlayStyle,
        }));
    };
    const changeHint = (e: ICheckboxEvent) => {
        let hint = e.target.checked;
        dispatch(editTask({ taskIndex, changingProp: "hint", val: hint }));
        dispatch(post("rhyt-name-hint", {
            planId: plan._id,
            index: taskIndex,
            hint,
        }));
    };

    const task = plan.tasks[taskIndex];
    const data = task.data as IRhytNotationData;
    const forwardProps = {
        plan,
        taskIndex,
        openChanger,
        handleChangeOpen,
        setPopupRef,
    }
    return (
        <div>
            {inlineEditorOpen && (
                <ScoreEditor
                    taskType="rhytNotation"
                    withSolfaPanel
                    score={JSON.parse(JSON.stringify(getScore(plan, task) || {}))}
                    scale={getScale(plan, task)}
                    tempo={getTempo(plan, task)}
                    setScore={storeScore}
                    changeTempo={changeTempo}
                />
            )}
            {!publicView && (
                <EditorBar>
                    <FlexLeft>
                        <ChangeScale {...forwardProps} />
                        <SelectWrapper>
                            <div>
                                <FormattedMessage {...messages.rhytPlayStyle} />:
                            </div>
                            <Select
                                name="rhytPlayStyle"
                                value={data.rhytPlayStyle || "auto"}
                                options={rhytPlayStyleOptions(formatMessage)}
                                noDelete
                                dark
                                onChange={changeRhytPlayStyle}
                            />
                        </SelectWrapper>
                    </FlexLeft>
                    <Center
                        disabled={(inlineEditorOpen && !playable)}
                        onClick={() => {
                            toggleInlineEditor(!inlineEditorOpen);
                            setPlayable(true);
                            if (inlineEditorOpen) {
                                exportScore();
                            }
                            setItem("rhytNameDone", "1", 60 * 60 * 24 * 365, "/");
                        }}
                        className={cn(
                            !(inlineEditorOpen && !playable) && !hasItem("rhytNameDone") && !task.ref && "pulsate"
                        )}
                    >
                        {inlineEditorOpen ? (
                            <FormattedMessage {...messages.stopEdit} />
                        ) : (
                            <FormattedMessage {...messages.editScore} />
                        )}
                    </Center>
                    <FlexRight>
                        <Changer>
                            <Checkbox
                                dark
                                label={<FormattedMessage {...messages.firstKnown} />}
                                checked={data.hint === undefined ? true : !!data.hint}
                                onChange={changeHint}
                            ></Checkbox>
                        </Changer>
                        <ChangeTempo {...forwardProps} />
                        <ChangeColor
                            {...forwardProps}
                            color={task.data?.color}
                        />
                    </FlexRight>
                </EditorBar>
            )}
        </div>
    );
}

export default RhytNotationEditor;
